import { HttpErrorResponse } from '@angular/common/http';
import { Signal } from '@angular/core';
import { QueryLoadingState } from '@globalconnect/query';
import { environment } from '../../../../../environments/environment';
import { FileMetaData } from '../../../../shared/installation-report-content-v2/self-inspection-v2/self-inspection-v2-entities';
import { SelfInspcetionWrapperData } from '../../report-inputs/report-input-entities';

export namespace v3 {
  export enum MarkerTypes {
    CABINET = 'CABINET',
    CABINET_NEW = 'CABINET_NEW',
    MANHOLE = 'MANHOLE',
    HP = 'HP',
    HC = 'HC',
    SITE = 'SITE',
    HUB = 'HUB',
    SPAN = 'SPAN',
  }

  export interface InspectionReportGroupsData {
    currentCount: number;
    items: InspectionReportGroup[];
  }

  export interface InspectionReportGroup {
    group: GroupObjectResponse;
    selfInspections: CombinedSelfInspections[];
  }

  export interface CombinedSelfInspections {
    answer: AnswerResponse | null;
    definition: Definition;
  }

  export interface ReportAnswer {
    data: any; // add type with all various type objects (text file etc)
    wrapperData: SelfInspcetionWrapperData;
  }

  export interface ReportRequest {
    type: MarkerTypes;
    ifsNumber: string;
    spanIds: string[];
  }

  export interface ReportRequestWithSignal extends Omit<
    ReportRequest,
    'spanIds'
  > {
    spanIds: Signal<string[]>;
  }

  export interface PaginatedReportsResponse {
    items: ReportResponse[];
    totalItems: number;
    totalPages: number;
    currentPage: number;
    currentCount: number;
  }

  export interface ReportResponse {
    id: string;
    type: MarkerTypes;
    markerId: string;
    ifsNumber: string;
    status: ReportStatus;
    comment: string;
    lastUpdateAt: Date;
    updatedBy: string;
    isDeviated: boolean;
    spans: string[] | null;
  }

  export interface DefinitionsWithGroupInfoResponse {
    items: DefinitionGroupResponse[];
    currentCount: number;
  }

  export interface DefinitionGroupResponse {
    groupInfo: GroupInfo;
    selfInspectionDefinitions: Definition[];
  }

  export interface GroupInfo {
    groupName: Groups;
    groupNameTranslations: IdentifierObject;
    isGrouped: boolean;
  }

  export interface GroupStatus {
    isCompleted: boolean;
    selfInspectionsDone: number;
    totalAmountOfSelfInspections: number;
  }

  export interface DefinitionWithAnswer extends Definition {
    answer: AnswerResponse | undefined | null;
  }

  export interface Definition {
    id: string;
    type: DefinitionType;
    subSection: IdentifierObject;
    description: IdentifierObject;
    file?: DefinitionFileObject;
    fileAndText?: DefinitionFileAndText;
    groupName: Groups;
    instructionalImage: string;
    list?: DefinitionListAndTextObject;
    text?: DefinitionListAndTextObject;
    textArea?: DefinitionListAndTextObject;
    radioButton?: {
      alternatives: RadioButtonAlterative[];
    };
  }

  export interface IdentifierObject {
    identifier: string;
    sv: string;
    fi: string;
    en: string;
  }

  export interface DefinitionFileAndText {
    file: DefinitionFileObject;
    text: DefinitionListAndTextObject;
  }

  export interface RadioButtonAlterative {
    label: IdentifierObject;
    value: string;
  }

  export interface DefinitionFileObject {
    mimeType: string;
    isImages: boolean;
  }

  export interface DefinitionListAndTextObject {
    placeholder: string;
    tooltip: string;
  }

  export interface AnswerRequestType {
    isNotApplicableComment?: string;
    checkBox?: boolean;
    fileAndText?: string;
    listTexts?: string[];
    radioButton?: string;
    text?: string;
  }

  export type SelfInspections = AnswerResponse[];

  export interface AnswerResponse {
    id: string;
    selfInspectionDefinitionId: string;
    completed: boolean;
    isNotApplicable: boolean;
    isNotApplicableComment: string | null;
    files: AnswerFileObject[] | null;
    fileAndText: AnswerFileAndText | null;
    text: string | null;
    textArea: string | null;
    list: string[] | null;
    radioButton: string | null;
    checkBox: boolean;
    lastUpdateAt: Date;
    updatedBy: string;
  }

  export interface AnswerWithGroupStatus {
    answer: AnswerResponse;
    groupStatus: GroupStatus;
  }

  export interface AnswerFileObject {
    fileId: string;
    fileName: string;
    size: number;
    mediaType: string;
  }

  export interface AnswerFileAndText {
    fileMetas: AnswerFileObject[];
    text: string;
  }

  export interface ReportGroupsResponse {
    items: AnswersByGroupItem[];
    currentCount: number;
  }

  export interface ReportGroupWithDefinitions extends AnswersByGroupItem {
    definitions: Definition[];
  }

  export interface AnswersByGroupItem {
    group: GroupObjectResponse;
    selfInspections: SelfInspections;
  }

  export interface GroupObjectResponse {
    groupInfo: GroupInfo;
    groupStatus: GroupStatus;
  }

  export interface FileResponse {
    items: FileMetaData[];
    currentCount: number;
  }

  export interface InitValues {
    ifs: string;
    typeId: MarkerTypes;
    markerId: string;
    urls: {
      report: InspectionReportUrlType;
      reportGroups: InspectionReportUrlType;
      answers: InspectionReportUrlType;
      files: InspectionReportUrlType;
    };
  }

  export const inspectionReportUrlTypes = {
    spanProgress: {
      /* have not added definitions here because we wont use it as separate call in v2, can be added though */
      report: `${environment.spanProgressApiUrlV3}/reports`,
      reportGroups: `${environment.spanProgressApiUrlV3}/reports`,
      answers: `${environment.spanProgressApiUrlV3}/reports`,
      files: `${environment.spanProgressApiUrlV3}/reports/files`,
    },
  } as const;

  export type InspectionReportUrlType =
    | typeof inspectionReportUrlTypes.spanProgress.report
    | typeof inspectionReportUrlTypes.spanProgress.reportGroups
    | typeof inspectionReportUrlTypes.spanProgress.answers
    | typeof inspectionReportUrlTypes.spanProgress.files;

  export type ReportsByMarkerType = Record<
    MarkerTypes,
    Record<string, ReportResponse>
  >;

  export interface ReportInfoData {
    lastUpdatedBy: string;
    referenceField: ReportInfoItem;
    type: MarkerTypes;
    status: string;
    showDeviationPill: boolean;
    isDeviation?: boolean;
    groups: ReportInfoGroup[];
  }

  export type ReportInfoGroup = ReportInfoItem[];

  export interface ReportInfoItem {
    label: string;
    values: string[];
  }

  export enum Groups {
    CABINET_ESTABLISHMENT = 'CABINET_ESTABLISHMENT',
    FIBER_OPTICS = 'FIBER_OPTICS',
    LABELING = 'LABELING',
    MANHOLE_ESTABLISHMENT = 'MANHOLE_ESTABLISHMENT',
    CABLES = 'CABLES',
    JOINT_BOX = 'JOINT_BOX',
    RESTORATION = 'RESTORATION',
    PLACEMENT_OF_SITE = 'PLACEMENT_OF_SITE',
    SITE_INSPECTION = 'SITE_INSPECTION',
    PLACEMENT_OF_HUB = 'PLACEMENT_OF_HUB',
    HUB_INSPECTION = 'HUB_INSPECTION',
    PERMIT = 'PERMIT',
    SPAN_DEVIATION = 'SPAN_DEVIATION',
    DROP_OFF_POINT = 'DROP_OFF_POINT',
    SETTING_CABINET = 'SETTING_CABINET',
    FIBER_WORK = 'FIBER_WORK',
    MARKING = ' MARKING',
    SETTING_WELL = 'SETTING_WELL',
    WELL_RESTORATION = 'WELL_RESTORATION',
  }

  export enum DeviationGroups {
    SPAN_DEVIATION = 'SPAN_DEVIATION',
  }

  export enum DefinitionType {
    CHECKBOX = 'CHECKBOX',
    FILES = 'FILES',
    FILE_AND_TEXT = 'FILE_AND_TEXT',
    LIST = 'LIST',
    TEXT = 'TEXT',
    RADIOBUTTON = 'RADIOBUTTON',
    TEXT_AREA = 'TEXT_AREA',
  }

  export enum ReportStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    DONE = 'DONE',
    DONE_WITH_DEVIATION = 'DONE_WITH_DEVIATION',
    UNKNOWN = 'UNKNOWN',
    DONE_WITHOUT_IMAGES = 'DONE_WITHOUT_IMAGES',
    PERMIT_DONE = 'PERMIT_DONE',
    PLACEMENT_OF_SITE_DONE = 'PLACEMENT_OF_SITE_DONE',
    SITE_INSPECTION_DONE = 'SITE_INSPECTION_DONE',
    PLACEMENT_OF_HUB_DONE = 'PLACEMENT_OF_HUB_DONE',
    HUB_INSPECTION_DONE = 'HUB_INSPECTION_DONE',
    NOT_STARTED = 'NOT_STARTED',
    PLANNED = 'PLANNED',
    RESTORED = 'RESTORED',
  }

  export enum SpanStaus {
    SELECTED = 'SELECTED',
    PLANNED = 'PLANNED',
    IN_PROGRESS = 'IN_PROGRESS',
    DONE = 'DONE',
    RESTORED = 'RESTORED',
    DEVIATED = 'DEVIATED',
  }

  export const SpanColor = {
    SELECTED: '#15FCFE',
    PLANNED: '#000000',
    IN_PROGRESS: '#8746FF',
    DONE: '#03FF39',
    RESTORED: '#008229',
    DEVIATED: '#FF0000',
    EXISTING: '#FFEE00',
  };

  export type CombinedQueryLoadingState = [
    definitionState: DefinitionQueryLoadingState,
    reportStates: [
      reportState: ReportQueryLoadingState,
      reportGroupsState: ReportGroupsQueryLoadingState,
    ],
  ];

  export type QueryLoadingStates = {
    definitionState: DefinitionQueryLoadingState;
    reportState: ReportQueryLoadingState;
    reportGroupsState: ReportGroupsQueryLoadingState;
  };

  export type InspectionGroupsAndReportLoadingState = {
    reportState: ReportQueryLoadingState;
    inspectionGroupsState: InspectionReportGroupsDataQueryLoadingState;
  };

  export type DefinitionQueryLoadingState = QueryLoadingState<
    DefinitionsWithGroupInfoResponse,
    HttpErrorResponse,
    DefinitionsWithGroupInfoResponse
  >;

  export type ReportQueryLoadingState = QueryLoadingState<
    ReportResponse,
    HttpErrorResponse,
    ReportResponse
  >;

  export type ReportGroupsQueryLoadingState = QueryLoadingState<
    ReportGroupsResponse,
    HttpErrorResponse,
    ReportGroupsResponse
  >;

  export type InspectionReportGroupsDataQueryLoadingState = QueryLoadingState<
    InspectionReportGroupsData,
    HttpErrorResponse,
    InspectionReportGroupsData
  >;
}
